import { createContext, useContext, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';

export interface Camera {
  id: string;
  title: string;
  url: string;
}

interface Settings {
  direction?: 'ltr' | 'rtl';
  language: 'en' | 'des' | 'es';
  pinSidebar: boolean;
  theme?: string;
  cameras?: Camera[];
  defaultCaptureMethod?: {
    protection?: string;
    verification?: string;
  };
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  direction: 'ltr',
  language: 'en',
  pinSidebar: true,
  theme: 'light',
  cameras: [],
  defaultCaptureMethod: {},
};

export const restoreSettings = (): Settings | null => {
  let settings = null;

  try {
    const storedData = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData);
    } else {
      settings = {
        direction: 'ltr',
        language: 'en',
        pinSidebar: true,
        // theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        theme: 'light',
        cameras: [],
        defaultCaptureMethod: {},
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  if (
    !settings.cameras?.find((camera) => camera.id === settings.defaultCaptureMethod?.protection)
  ) {
    settings.defaultCaptureMethod = {
      ...settings.defaultCaptureMethod,
      protection: undefined,
    };
  }

  if (
    !settings.cameras?.find((camera) => camera.id === settings.defaultCaptureMethod?.verification)
  ) {
    settings.defaultCaptureMethod = {
      ...settings.defaultCaptureMethod,
      verification: undefined,
    };
  }

  return settings;
};

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {},
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SettingsConsumer = SettingsContext.Consumer;

export const useSettings = () => useContext(SettingsContext);
